/**
 * APP: Taako.
 *
 * Builds the PIXI stage and start loads.
 * Auto starts the background audio
 * Renders the intial game state.
 *
 * PIXIJS -> https://pixijs.io/examples/#/basics/basic.js
 * PIXIE SOUND ENGINE -> https://pixijs.io/
 * REACT_PIXI -> https://github.com/inlet/react-pixi
 * NOTE: IS_PRODUCTION to true in constants to hide DAT-GUI in production
 *
 */

import React from 'react';
import './App.css';
import constants from './Constants';

import * as PIXI from 'pixi.js';
import { Stage, Container, Text, withPixiApp } from '@inlet/react-pixi';

import { Clouds } from './Layers/Clouds';
import { GrassContainer } from './Layers/Grass';
import { VignetteContainer } from './Layers/Vignette';
import { Particles } from './Layers/Particles';
import { StoneContainer } from './Layers/Rock';
import { LightContainer } from './Layers/Light';

import TweenMax from 'gsap/TweenMax'
import parser from 'ua-parser-js';

import sono from 'sono';
import 'sono/utils';

import { TranscriptLoader } from './TranscriptLoader';

const CloudContainer = withPixiApp( Clouds );
const ParticleContainer = withPixiApp( Particles );

const OPTIONS_PIXI = {
  transparent: true
}

const ua = new parser();

const stylePreloader = new PIXI.TextStyle({
  fontFamily: 'Arial',
  fontSize: 14,
  fontStyle: 'normal',
  fontWeight: 'normal',
  lineHeight: 20,
  textBaseline: 'alphabetic',
  fill: ['#FFFFFF', '#FFFFFF'], // gradient
  stroke: '#FFF',
  strokeThickness: 0,
  dropShadow: true,
  dropShadowColor: '#000000',
  dropShadowBlur: 4,
  dropShadowAngle: 45,
  dropShadowDistance: 0,
  wordWrap: true,
  wordWrapWidth: 440
});

class App extends React.Component {

  constructor(props) {
    super(props);

    this.pixiRenderObj = null;
    this.canvasObj = null;

    // TODO: Change this, will have many sounds.
    //       perhaps do a sound sprite for the other sounds
    this.sound = null;

    this.backgroundAudio = null;
    this.backgroundAudio_1 = null;
    this.backgroundAudio_2 = null;
    this.voiceAudio = null;
    this.backgroundAudioVolume = 0.8; //1;
    this.exposedVoiceOverVolume = 1;

    this.voiceAudioIsPlaying = false;

    // for Rock. need to call a child method
    this.rock = React.createRef();
    this.light = React.createRef();

    this.assetsPath = './Assets/'; // 'https://www.taakoschool.com/qa/Assets/';

    this.state = {
      appReady: false, // app loaded start engine
      startLoad: false,
      loadingProgress: '0%',
      sw:0,
      sh:0,
      showFadeIn: true,
      ua: ua.getResult(),
      audioIsActive: false // If the user hit a valid code, triggers the Light rays
    };

  }

  componentDidMount() {
    // console.log('UA: ', this.state.ua )

    // Merge DATA from constants
    // and if its production hide Dat.GUI UI
    this.setState(() => {
      return { ...constants.LAYER_DATA }
    });
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if ( prevState.sw !== nextProps.sw ||
      prevState.sh !== nextProps.sh || prevState.startLoad !== nextProps.startApp ) {
      return {
        sw: nextProps.sw,
        sh: nextProps.sh,
        fontSize: 12,
        startLoad: nextProps.startApp
      };
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState){
    if ( prevState.startLoad !== this.props.startApp ) {
      // this.fadeInPreloader();
      this.startLoad();
      this.preload_UIAudio();
    }
  }

  // Gets values from react context.
  resize = () => {
    if( this.pixiRenderObj !== null ) {
      this.pixiRenderObj.renderer.resize( this.props.sw, this.props.sh );
    }
  }

  // PIXI-React node has mounted we can start loading assets
  pixiRenderMount = (renderer, canvas) => {
    this.pixiRenderObj = renderer
    this.canvasObj = canvas
  }

  /**
   * 5120x2880
   * 5K/
   *
   * 3840x2160
   * 4K/
   *
   * 2560x1440
   * 1440p/
   *
   * 1920x1080
   * 1080p/
   */

  startLoad = () => {

    //console.log('PIXI - START GRAPHICS LOADING');

    // this.assetsPath
    let activeAssetsFolder = this.assetsPath + 'Image/scene_elements/';
    let activeViewport = '1080p/';

    let cloud1 = '01-cloud01.png';
    let cloud2 = '01-cloud02.png';
    let cloud3 = '01-cloud03.png';
    let mountains = '02-mountainrange.png';
    let lightRays = '03-lightrays.png';
    let grassFar = '04-grassfar.png';
    let grassMid = '05-grassmid.png';
    let grassNear = '07-grassnear.png';
    // let mote = '08-mote.png';
    let digitLight = 'digit.png';
    let stoneActive = 'stone-active.png';
    let stoneHover = 'stone-hover.png';
    let stone = 'stone-inactive.png';


    PIXI.loader.add('cloud1', activeAssetsFolder + activeViewport + cloud1 )
    PIXI.loader.add('cloud2', activeAssetsFolder + activeViewport + cloud2 )
    PIXI.loader.add('cloud3', activeAssetsFolder + activeViewport + cloud3 )
    PIXI.loader.add('rock', activeAssetsFolder + activeViewport + stone )
    PIXI.loader.add('digit', activeAssetsFolder + activeViewport + digitLight )
    PIXI.loader.add('rock_on', activeAssetsFolder + activeViewport + stoneActive )
    PIXI.loader.add('rock_over', activeAssetsFolder + activeViewport + stoneHover )
    PIXI.loader.add('light', activeAssetsFolder + activeViewport + lightRays )
    PIXI.loader.add('grassFar', activeAssetsFolder + activeViewport + grassFar )
    PIXI.loader.add('grassNear', activeAssetsFolder + activeViewport + grassNear )
    PIXI.loader.add('grassMid', activeAssetsFolder + activeViewport + grassMid )
    PIXI.loader.add('mountains', activeAssetsFolder + activeViewport + mountains )

    // Watch for faux load progress
    PIXI.loader.onProgress.add((loader)=>{
      this.setState({
        loadingProgress: Math.floor( loader.progress ) + '%'
      })
    });

    // Ready !
    PIXI.loader.load(() => {
      // this.setup();
      // this.fadeOutPreloader();
      // console.log('PIXI - GRAPHICS HAVE LOADED');
      this.startAudio();
    });

  }

  setup = () => {
    this.setState({
      appReady: true
    }, () => {
      this.fadeIn();
    })
  }

  fadeIn = () => {
    // console.log('SONO - PLAYING INTRO -> FADE TO APP');
    TweenMax.to("#intro-container", 1, {opacity:0, display:'none'});
    TweenMax.to("#app-fadein-anim", 3, {opacity:0, delay:0.75 ,display:'none', onComplete: this.fadeInComplete});

    let el = document.getElementById('sub-navigation-container');
    el.style.opacity = '0';
    el.style.display = 'block';
    TweenMax.to("#sub-navigation-container", 1, {opacity:1, delay:1.75});

  }

  fadeInComplete = () => {
    this.setState({
      showFadeIn: false
    }, () => {
      // this.startAudio()
    })
  }

  playIntroAudio = () => {
    // sometimes we need a delay to start the fade first....
    this.introAudio.play();
    this.setup();
  }

  startAudio = () => {

    // console.log('SONO - LOADING AND STARTING AUDIO FILES');

    let activeAssetsFolder = this.assetsPath + 'Audio/';

    this.backgroundAudio = sono.create({
      id: 'backgroundAudio',
      url: [ activeAssetsFolder + 'atmosfield.ogg', activeAssetsFolder + 'atmosfield.mp3'],
      loop: true,
      volume: this.backgroundAudioVolume
    });
    this.backgroundAudio_1 = sono.create({
      id: 'backgroundAudio',
      url: [ activeAssetsFolder + 'atmos1.ogg', activeAssetsFolder + 'atmos1.mp3'],
      loop: true,
      volume: this.backgroundAudioVolume
    });
    this.backgroundAudio_2 = sono.create({
      id: 'backgroundAudio',
      url: [ activeAssetsFolder + 'atmos2.ogg', activeAssetsFolder + 'atmos2.mp3'],
      loop: true,
      volume: this.backgroundAudioVolume
    });

    let fileName = this.getIntroAudioFileName();
    this.introAudio = sono.create({
      id:'intro',
      volume: this.exposedVoiceOverVolume,
      onComplete: ( sound ) => {
        // console.log('INTRO AUDIO LOADED',sound);
        },
      onProgress: ( progress ) => {
        // console.log('INTRO AUDIO LOADING', progress);
        this.playIntroAudio();
        },
      url: [ activeAssetsFolder + 'Bucket/'+ fileName +'.ogg', activeAssetsFolder + 'Bucket/'+ fileName +'.mp3']
    }).once('ended', this.destroyIntroAudioInstance );

    // Background audio start
    this.backgroundAudio.play();
    this.backgroundAudio_1.play();
    this.backgroundAudio_2.play();

    try {
      TranscriptLoader(fileName).then((r) => {
        if (r.statusText === 'OK' && r.status === 200) {
          this.props.updateTranscriptModal(r.data);
        } else {
          this.props.updateTranscriptModal(
            '<p>there was an error getting the transcript</p>');
        }
      }).catch((e) => {
        this.props.updateTranscriptModal(
          '<p>there was an error getting the transcript</p>');
      })

    } catch (err) {
      // console.log('Server Error Returning the Transcript', fileName );
    }


  }

  getIntroAudioFileName = () => {
    let fileList = ['AAS100-01', // 15
      'AAS100-02', // 15
      'AAS100-03', // 15
      'AAS100-04', // 15
      'AAS100-05', // 15
      'AAS100-06', // 10
      'AAS100-07', // 6
      'AAS100-08', // 3
      'AAS100-09', // 3
      'AAS100-10'] // 3
    let weightList = [15,15,15,15,15,10,6,3,3,3];
    let totalWeight = 15+15+15+15+15+10+6+3+3+3;
    let randomIntroId = Math.floor(Math.random() * totalWeight);
    for (let i = 0; i < fileList.length; i++) {
      randomIntroId -= weightList[i];
      if (randomIntroId < 0) {
        return fileList[i];
      }
    }
  }

  destroyIntroAudioInstance = () => {
    sono.destroy('intro');
    this.introAudio = null;
  }

  playAudioFile = ( file, id ) => {

    if (this.voiceAudioIsPlaying === true) return

    this.props.updateTranscriptModal('');

    let fileToPlay = '';

      if (id === 1) {
        fileToPlay = file.a1;
        this.voiceAudio = sono.create({
          id: 'single',
          volume: this.exposedVoiceOverVolume,
          url: [
            './Assets/Audio/Bucket/' + fileToPlay + '.ogg',
            './Assets/Audio/Bucket/' + fileToPlay + '.mp3']
        }).once('ended', this.destroyAudioInstance);
      }

      if ( id === 2) {
        // we dont have just 1 extra to play we have 1 new use case
        // where its 1 code. but 12 cooking tips. to select randomly
        // -> cooktips -> change from random to seq...
        // fileToPlay = file.a2[Math.floor(Math.random() * file.a2.length)];
        fileToPlay = file.a2[file.playAt];
        this.voiceAudio = sono.create({
          id: 'single',
          volume: this.exposedVoiceOverVolume,
          url: [
            './Assets/Audio/Bucket/' + fileToPlay + '.ogg',
            './Assets/Audio/Bucket/' + fileToPlay + '.mp3']
        }).once('ended', this.destroyAudioInstance);
      }

      if( id === 3 ) {
        // special case for 1 code.
        fileToPlay = file;
        this.voiceAudio = sono.create({
          id: 'single',
          volume: this.exposedVoiceOverVolume,
          url: [
            './Assets/Audio/Bucket/' + fileToPlay + '.ogg',
            './Assets/Audio/Bucket/' + fileToPlay + '.mp3']
        }).once('ended', this.destroyAudioInstance);
      }

      sono.play('single');
      this.voiceAudioIsPlaying = true;

      this.light.current.activate();

    try {
      TranscriptLoader(fileToPlay).then((r) => {
        if (r.statusText === 'OK' && r.status === 200) {
          this.props.updateTranscriptModal(r.data);
        } else {
          this.props.updateTranscriptModal(
            '<p>there was an error getting the transcript</p>');
        }
      }).catch((e) => {
        this.props.updateTranscriptModal(
          '<p>there was an error getting the transcript</p>');
      })

      this.duckAudio('out');
    } catch (err) {
      // console.log('Server Error Returning the Transcript', fileToPlay);
    }
  }

  destroyAudioInstance = ( sObj ) => {
    sono.destroy('single');
    this.voiceAudio = null;
    this.rock.current.clearCurrentMsg();
    this.voiceAudioIsPlaying = false;
    this.duckAudio('in');
    this.light.current.deActivate();
  }

  duckAudio = ( dir ) => {

    // console.log('AUDIO -> ',this.backgroundAudio )

    if( dir === 'out' ) {
      this.backgroundAudio.fade(0,1);
      this.backgroundAudio_1.fade(0,1);
      this.backgroundAudio_2.fade(0,1);
    } else {
      this.backgroundAudio.fade(this.backgroundAudioVolume,1);
      this.backgroundAudio_1.fade(this.backgroundAudioVolume,1);
      this.backgroundAudio_2.fade(this.backgroundAudioVolume,1);
    }
  }

  preload_UIAudio = () => {

    let activeAssetsFolder = this.assetsPath + 'Audio/';

    // hydrate all btn audio ->
    let btns = constants.UI_AUDIO.btnClicksOk;
    let totalBtns = btns.length;
    let sonoLoaderObj = [];

    for (let i = 0; i < totalBtns; i++) {

      sonoLoaderObj.push(
        { id:btns[i].name , url:activeAssetsFolder + 'UI/'+ btns[i].fileName +'.mp3' }
      )

    }

    sono.load({
      url:sonoLoaderObj,
      // onComplete: sounds => console.log(sounds),
      // onProgress: progress => console.log(progress)
    });

  }

  playUIAudio = ( audioId ) => {
    sono.play( audioId );
  }

  render() {

    // on any resize event pass this directly into PIXIE.
    this.resize()

    // render Taako
    return (

      <div className="App">


        { this.state.showFadeIn && <div id="app-fadein-anim" className="app-fadein-anim" /> }

        {/*
        <div id={"preloader-container"}>
          <p>{'loading progress: ' + this.state.loadingProgress}</p>
        </div>
        */}

        <Stage
          width={this.props.sw}
          height={this.props.sh}
          options={OPTIONS_PIXI}
          onMount={(renderer, canvas) => this.pixiRenderMount(renderer, canvas)}>

          {

            (!this.state.appReady) ?
              <Container>
                {/* This never gets show. - Need to fix that. POST into -> to here */}
                <Text style={stylePreloader} x={30} y={20}
                      text={'loading progress: ' +
                      this.state.loadingProgress}/>
              </Container>

              :

              /* RENDER APP */
              <Container>

                {/* Container that groups the various small clouds together */}
                <CloudContainer cloudData={this.state.clouds}
                                texture={[
                                  PIXI.loader.resources.cloud1.texture,
                                  PIXI.loader.resources.cloud2.texture,
                                  PIXI.loader.resources.cloud3.texture]}/>

                {/* Spinning light thing */}

                <LightContainer
                                ref={this.light}
                                lightBurstData={this.state.lightBurst}
                                texture={PIXI.loader.resources.light.texture}
                                active={(this.state.audioIsActive)?true:false}
                                screen={{
                                  sw: this.props.sw,
                                  sh: this.props.sh
                                }}/>


                {/* Grass texture container. Note: tile sprite.  */}
                <GrassContainer grassData={this.state.grass}
                                texture={[
                                  PIXI.loader.resources.grassNear.texture,
                                  PIXI.loader.resources.grassMid.texture,
                                  PIXI.loader.resources.grassFar.texture,
                                  PIXI.loader.resources.mountains.texture]}
                                screen={{
                                  sw: this.props.sw,
                                  sh: this.props.sh
                                }}/>

                {/* Actual main container. Contains the rock, rock-buttons, 'whats pressed view' */}

                <StoneContainer rockData={this.state.rock}
                               ref={this.rock}
                               screen={{sw: this.props.sw, sh: this.props.sh}}
                               texture={[
                                 PIXI.loader.resources.rock.texture,
                                 PIXI.loader.resources.rock_on.texture,
                                 PIXI.loader.resources.rock_over.texture,
                                 PIXI.loader.resources.digit.texture
                               ]}
                               playUIAudio={(id) => {
                                 this.playUIAudio(id)
                               }}
                               playAudioFile={(file, id) => {
                                 this.playAudioFile(file, id)
                               }}/>


                {/* Testing a particle engine that has a pixi render plugin */}
                <ParticleContainer/>

                {/* Vignette - Experimental uses a canvas to draw first */}
                <VignetteContainer alpha={this.state.vignette.alpha} screen={{
                  sw: this.props.sw,
                  sh: this.props.sh
                }}/>

              </Container>

          }

        </Stage>

      </div>
    )
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js